// Section 19: event choices answered in phase 2 are applied here, before production, so a
// choice like "burn the blighted fields" acts on this season (ADR-0010). Each event prompt from
// an earlier season is resolved: with the player's answer, or with its `defaultChoiceId` when the
// Decision carried none (headless runs; the UI blocks End season until every prompt is answered).
// The choice's `apply` hook returns deltas; its randomness comes from the seed the prompt carries
// (a sub-stream, so the event deck stays decision-independent, ADR-0016). An `event-choice` entry
// records the answer (the hook may record its own, with what happened). Arrival prompts decide a
// group at the gate (Section 14). Other prompt kinds (justice, pairing) are left for their phases.
import { createRng, hashString } from '../core/rng';
import type { Delta, EventPrompt, Phase, PhaseContext, Prompt, PromptAnswer } from '../core/types';
import { applyDeltas } from './shared/deltas';

type EventAnswer = Extract<PromptAnswer, { kind: 'event' }>;

function seedOf(prompt: EventPrompt): number {
  const seed = prompt.params.seed;
  return typeof seed === 'number' ? seed : hashString(prompt.id);
}

function answerFor(ctx: PhaseContext, prompt: EventPrompt): EventAnswer {
  const given = ctx.answers[prompt.id];
  const offered = (id: string): boolean => prompt.choices.some((c) => c.id === id);
  if (given?.kind === 'event' && offered(given.choiceId)) return given;
  return { kind: 'event', choiceId: prompt.defaultChoiceId, personIds: [] };
}

function records(deltas: readonly Delta[], prompt: EventPrompt): boolean {
  return deltas.some(
    (d) => d.kind === 'report' && d.entry.kind === 'event-choice' && d.entry.promptId === prompt.id,
  );
}

export const eventChoicesPhase: Phase = {
  id: 'event-choices',
  reads: ['openPrompts', 'promptAnswers', 'people', 'arrivalGroups', 'stocks', 'buildings'],
  writes: [
    'openPrompts',
    'currentReport',
    'people',
    'households',
    'formerPeople',
    'stocks',
    'seedReserved',
    'buildings',
    'activeEffects',
    'arrivalGroups',
    'counters',
    'quarantine',
    'factionMarks',
    'improvements',
    'nextIds',
    'safety',
  ],
  projection: 'skip',
  run(ctx) {
    const resolved = new Set<string>();
    for (const prompt of ctx.state.openPrompts as readonly Prompt[]) {
      if (prompt.kind !== 'event' || prompt.turn >= ctx.season.turn) continue;
      resolved.add(prompt.id);
      const def = ctx.registry.events[prompt.eventId];
      const answer = answerFor(ctx, prompt);
      const choice = def.choices.find((c) => c.id === answer.choiceId);
      if (choice === undefined) continue;
      const deltas = choice.apply(ctx.live, prompt, answer);
      if (!records(deltas, prompt)) {
        ctx.report.add({
          kind: 'event-choice',
          eventId: prompt.eventId,
          promptId: prompt.id,
          choiceId: choice.id,
        });
      }
      const scoped: PhaseContext = {
        ...ctx,
        eventRng: createRng((seedOf(prompt) ^ hashString('choice-rng')) >>> 0).rng,
      };
      applyDeltas(scoped, deltas);
    }
    if (resolved.size > 0)
      ctx.draft.openPrompts = ctx.draft.openPrompts.filter((p) => !resolved.has(p.id));
    return 'done';
  },
};
